import { Link } from 'react-router-dom';
import useSEO from '../hooks/useSEO';
import { STATES } from '../utils/states';

// "Arizona and Kansas" — derived from the states we're actually live in, so this
// copy doesn't need hand-editing every time a state launches.
const LIVE_STATE_LIST = (() => {
  const names = Object.values(STATES).filter(s => s.live).map(s => s.name);
  if (names.length <= 1) return names[0] || '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
})();

const pageStyle = {
  hero: { background: 'linear-gradient(135deg, #1a3a3a 0%, #1d4142 100%)', padding: '60px 0' },
  heroInner: { maxWidth: 760, margin: '0 auto', padding: '0 24px', textAlign: 'center' },
  heroTitle: { fontFamily: 'var(--font-display)', fontSize: 'clamp(1.8rem, 3vw, 2.8rem)', color: 'white', marginBottom: 16 },
  heroSub: { fontSize: 18, color: 'rgba(255,255,255,0.75)', lineHeight: 1.6 },
  wrapper: { padding: '56px 0 80px' },
  container: { maxWidth: 760, margin: '0 auto', padding: '0 24px' },
  body: { fontSize: 16, lineHeight: 1.8, color: 'var(--slate-700)' },
  h2: { fontFamily: 'var(--font-display)', fontSize: 22, color: 'var(--forest-700)', margin: '40px 0 14px', paddingBottom: 10, borderBottom: '2px solid #b39e89' },
  infoBox: { background: 'var(--tan-50)', border: '1.5px solid #b39e89', borderRadius: 12, padding: '20px 24px', marginBottom: 24 },
  step: { background: 'var(--tan-50)', border: '1.5px solid #b39e89', borderRadius: 12, padding: '20px 24px', marginBottom: 16, display: 'flex', gap: 16, alignItems: 'flex-start' },
  stepNum: { width: 32, height: 32, background: 'var(--forest-500)', color: 'white', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 15, flexShrink: 0 },
};

export function AboutPage() {
  useSEO({ title: 'About Us | Steady Education', description: `Learn about Steady Education — trusted online continuing education for ${LIVE_STATE_LIST} real estate licensees.`, url: 'https://steadyeducation.com/about' });
  return (
    <div>
      <section style={pageStyle.hero}>
        <div style={pageStyle.heroInner}>
          <h1 style={pageStyle.heroTitle}>About Steady Education</h1>
          <p style={pageStyle.heroSub}>Trusted real estate continuing education for {LIVE_STATE_LIST} licensees — expanding nationwide.</p>
        </div>
      </section>
      <div style={pageStyle.wrapper}>
        <div style={pageStyle.container}>
          <div style={pageStyle.infoBox}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24 }}>
              {[
                ['🏛️', 'Arizona School License', 'S25-0002'],
                ['🏛️', 'Kansas School ID', 'ED0190'],
                ['📍', 'Location', '5227 N 7th St, Phoenix, AZ 85014'],
                ['📧', 'Email', 'help@steadyeducation.com'],
                ['✓', 'ADRE & KREC Approved', 'Provider'],
              ].map(([icon, label, val]) => (
                <div key={label} style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <span style={{ fontSize: 20 }}>{icon}</span>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--forest-700)' }}>{val}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div style={pageStyle.body}>
            <p>Steady Education was built for real estate licensees who want a no-nonsense way to complete their CE requirements. Every course is approved by the appropriate state regulatory authority and designed to be genuinely useful — not just checkbox compliance.</p>

            <h2 style={pageStyle.h2}>Our Mission</h2>
            <p>To provide the most accessible, affordable, and high-quality continuing education for real estate professionals across the United States. We believe staying educated should be easy, not a burden.</p>

            <h2 style={pageStyle.h2}>Why Choose Us?</h2>
            <ul style={{ paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
              {[
                'All courses approved by state regulatory authorities',
                'Fully self-paced — study on your schedule',
                'Starting at $9.99 per course',
                'State-specific content written for local licensees',
                'Instant certificate upon completion',
                'Simple, clean interface — no tech headaches',
              ].map(i => <li key={i}>{i}</li>)}
            </ul>

            <h2 style={pageStyle.h2}>Expanding Nationwide</h2>
            <p>Currently offering courses in Arizona and Kansas, with Utah and New Jersey coming soon. We are working hard to bring state-approved CE to real estate licensees across the country.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export function ContactPage() {
  useSEO({ title: 'Contact Us | Steady Education', description: `Contact Steady Education with questions about ${LIVE_STATE_LIST} real estate CE courses, your account or your certificates. We're here to help.`, url: 'https://steadyeducation.com/contact' });
  return (
    <div>
      <section style={pageStyle.hero}>
        <div style={pageStyle.heroInner}>
          <h1 style={pageStyle.heroTitle}>Contact Us</h1>
          <p style={pageStyle.heroSub}>We're here to help with any questions about your CE courses.</p>
        </div>
      </section>
      <div style={pageStyle.wrapper}>
        <div style={pageStyle.container}>
          <div style={pageStyle.infoBox}>
            <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 20, color: 'var(--forest-700)' }}>Get in Touch</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              {[
                ['📧', 'Email', 'help@steadyeducation.com', 'mailto:help@steadyeducation.com'],
                ['📞', 'Customer Service', '(602) 714-1794', 'tel:+16027141794'],
                ['📍', 'Address', '5227 N 7th Street, Phoenix, AZ 85014', null],
                ['🏛️', 'License', 'Arizona School License S25-0002', null],
                ['🏛️', 'Kansas School ID', 'ED0190', null],
                ['🌐', 'Website', 'www.steadyeducation.com', 'https://steadyeducation.com'],
              ].map(([icon, label, val, href]) => (
                <div key={label} style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                  <span style={{ fontSize: 24 }}>{icon}</span>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>{label}</div>
                    {href ? <a href={href} style={{ color: 'var(--forest-600)', fontWeight: 500 }}>{val}</a> : <span style={{ color: 'var(--slate-700)', fontWeight: 500 }}>{val}</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div style={{ background: 'var(--forest-50)', border: '1.5px solid #b39e89', borderRadius: 12, padding: '16px 20px' }}>
            <span style={{ fontSize: 16, marginRight: 8 }}>💡</span>
            <span style={{ fontSize: 14, color: 'var(--forest-800)' }}>For certificate or completion issues, email us with your name and course title. We respond within one business day.</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export function HowToRenewPage() {
  useSEO({ title: 'How to Renew Your Arizona Real Estate License | Steady Education', description: 'Step-by-step guide to renewing your Arizona real estate license. Learn about ADRE CE requirements, deadlines, and how to complete your hours online.', url: 'https://steadyeducation.com/how-to-renew' });
  const steps = [
    { title: 'Check Your Renewal Date', desc: 'Real estate licenses must be renewed every two years. Log in to your state regulatory portal to confirm your renewal deadline.' },
    { title: 'Complete Required CE Hours', desc: 'You need to complete the required hours of approved continuing education for your state. Each Steady Education course is 3 credit hours.' },
    { title: 'Take the Course Exams', desc: 'Each course includes a short exam. You must achieve a 70% passing score. You have multiple attempts if needed.' },
    { title: 'Download Your Certificates', desc: 'Upon passing, your certificate of completion is immediately available. Download and save each certificate for your records.' },
    { title: 'Submit to Your State Board', desc: 'Log in to your state regulatory portal and submit your CE certificates. Pay your renewal fee and your license will be renewed.' },
  ];
  return (
    <div>
      <section style={pageStyle.hero}>
        <div style={pageStyle.heroInner}>
          <h1 style={pageStyle.heroTitle}>How to Renew Your Real Estate License</h1>
          <p style={pageStyle.heroSub}>A simple step-by-step guide to completing your CE requirements and renewing your license.</p>
        </div>
      </section>
      <div style={pageStyle.wrapper}>
        <div style={pageStyle.container}>
          {steps.map((s, i) => (
            <div key={i} style={pageStyle.step}>
              <div style={pageStyle.stepNum}>{i + 1}</div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 6, color: 'var(--forest-700)' }}>{s.title}</div>
                <div style={{ fontSize: 15, color: 'var(--slate-600)', lineHeight: 1.6 }}>{s.desc}</div>
              </div>
            </div>
          ))}

          <div style={{ ...pageStyle.infoBox, marginTop: 40 }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--forest-700)', marginBottom: 12 }}>🏛️ Arizona Specific Instructions</h3>
            <p style={{ fontSize: 14, color: 'var(--slate-600)', lineHeight: 1.6, marginBottom: 12 }}>
              Once courses are completed, log in to your ADRE portal at <a href="https://ptl.az.gov/dre/#/login" target="_blank" rel="noreferrer" style={{ color: 'var(--forest-600)' }}>ptl.az.gov</a> and submit your certificates.
            </p>
            <p style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>Arizona School License: S25-0002</p>
          </div>

          <div style={{ marginTop: 40, textAlign: 'center' }}>
            <Link to="/arizona/courses" className="btn btn-primary btn-lg">Start Your CE Courses →</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export function TermsPage() {
  useSEO({ title: 'Terms of Service | Steady Education', description: `The terms of service for Steady Education, the online real estate continuing education school approved in ${LIVE_STATE_LIST}.`, url: 'https://steadyeducation.com/terms' });
  return (
    <div>
      <section style={pageStyle.hero}>
        <div style={pageStyle.heroInner}>
          <h1 style={pageStyle.heroTitle}>Terms of Service</h1>
          <p style={pageStyle.heroSub}>Last updated: {new Date().getFullYear()}</p>
        </div>
      </section>
      <div style={pageStyle.wrapper}>
        <div style={pageStyle.container}>
          <div style={pageStyle.body}>
            {[
              ['1. Acceptance of Terms', 'By using Steady Education, you agree to these terms. If you do not agree, please do not use our services.'],
              ['2. Course Access', 'Upon purchase, you receive lifetime access to your purchased courses. Courses may not be shared, resold, or transferred.'],
              ['3. Refund Policy', 'We offer refunds within 10 business days of purchase if you have not progressed beyond the first slide of any course. Contact help@steadyeducation.com.'],
              ['4. CE Credit', 'Steady Education courses are approved by state regulatory authorities for CE credit. Credit is contingent on passing the required course exams.'],
              ['5. Privacy', 'We collect only the information necessary to provide CE services. We do not sell your personal information. Payment processing is handled securely by Stripe.'],
              ['6. Contact', 'Questions: help@steadyeducation.com | Arizona School License S25-0002 | Kansas School ID ED0190'],
            ].map(([title, text]) => (
              <div key={title}>
                <h2 style={pageStyle.h2}>{title}</h2>
                <p>{text}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export function NotFoundPage() {
  return (
    <div>
      <section style={pageStyle.hero}>
        <div style={pageStyle.heroInner}>
          <h1 style={{ ...pageStyle.heroTitle, fontSize: 80 }}>404</h1>
          <h2 style={{ ...pageStyle.heroTitle, fontSize: 28 }}>Page Not Found</h2>
          <p style={pageStyle.heroSub}>The page you're looking for doesn't exist.</p>
          <Link to="/" className="btn btn-lg" style={{ background: '#b39e89', color: 'white', marginTop: 24, display: 'inline-flex' }}>Go Home</Link>
        </div>
      </section>
    </div>
  );
}

export default AboutPage;